import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { InventoryCluster } from '../../../types/inventory';
import { mockInventoryService } from '../../../services/mockInventoryService';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { confirmDelete, swalError } from '../../common/confirm';

interface InventoryClustersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  cluster_code: string;
  cluster_name: string;
  cluster_description: string;
  cluster_type: 'MODULE' | 'FEATURE' | 'SYSTEM' | 'WORKFLOW' | 'COMPONENT';
  business_domain: string;
  parent_cluster_id: number;
  cluster_status: 'ACTIVE' | 'PLANNED' | 'IN_PROGRESS' | 'DEPRECATED' | 'ARCHIVED';
  priority_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  responsible_team: string;
  complexity_estimate: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  completion_percentage: number;
  documentation_notes: string;
  is_active: boolean;
}

interface FormErrors {
  cluster_code?: string;
  cluster_name?: string;
  cluster_description?: string;
  completion_percentage?: string;
}

const initialFormData: FormData = {
  cluster_code: '',
  cluster_name: '',
  cluster_description: '',
  cluster_type: 'MODULE',
  business_domain: '',
  parent_cluster_id: 0,
  cluster_status: 'PLANNED',
  priority_level: 'MEDIUM',
  responsible_team: '',
  complexity_estimate: 'MEDIUM',
  completion_percentage: 0,
  documentation_notes: '',
  is_active: true
};

const InventoryClustersModal: React.FC<InventoryClustersModalProps> = ({ isOpen, onClose }) => {
  const [clusters, setClusters] = useState<InventoryCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCluster, setEditingCluster] = useState<InventoryCluster | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mockInventoryService.getClusters();
      setClusters(response.data);
    } catch (error) {
      console.error('Error loading inventory clusters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingCluster(null);
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  const handleAddClick = () => {
    setEditingCluster(null);
    setFormData(initialFormData);
    setErrors({});
    setShowForm(true);
  };

  const handleEditClick = (cluster: InventoryCluster) => {
    setEditingCluster(cluster);
    setFormData({
      cluster_code: cluster.cluster_code,
      cluster_name: cluster.cluster_name,
      cluster_description: cluster.cluster_description || '',
      cluster_type: cluster.cluster_type,
      business_domain: cluster.business_domain || '',
      parent_cluster_id: cluster.parent_cluster_id || 0,
      cluster_status: cluster.cluster_status,
      priority_level: cluster.priority_level,
      responsible_team: cluster.responsible_team || '',
      complexity_estimate: cluster.complexity_estimate || 'MEDIUM',
      completion_percentage: cluster.completion_percentage,
      documentation_notes: cluster.documentation_notes || '',
      is_active: cluster.is_active
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDeleteClick = async (cluster: InventoryCluster) => {
    const ok = await confirmDelete({ title: `Delete cluster "${cluster.cluster_name}"?` });
    if (!ok) return;
    try {
      await mockInventoryService.deleteCluster(cluster.id);
      await loadData();
    } catch (error) {
      console.error('Error deleting inventory cluster:', error);
      swalError('Failed to delete cluster. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.cluster_code.trim()) {
      newErrors.cluster_code = 'Cluster code is required';
    } else if (formData.cluster_code.length > 50) {
      newErrors.cluster_code = 'Cluster code must be 50 characters or less';
    }

    if (!formData.cluster_name.trim()) {
      newErrors.cluster_name = 'Cluster name is required';
    } else if (formData.cluster_name.length > 100) {
      newErrors.cluster_name = 'Cluster name must be 100 characters or less';
    }

    if (!formData.cluster_description.trim()) {
      newErrors.cluster_description = 'Description is required';
    } else if (formData.cluster_description.length > 1000) {
      newErrors.cluster_description = 'Description must be 1000 characters or less';
    }

    if (formData.completion_percentage < 0 || formData.completion_percentage > 100) {
      newErrors.completion_percentage = 'Completion percentage must be between 0 and 100';
    }

    // Check for duplicate codes/names (excluding current item when editing)
    const existingCluster = clusters.find(c => 
      (c.cluster_code.toLowerCase() === formData.cluster_code.toLowerCase() ||
       c.cluster_name.toLowerCase() === formData.cluster_name.toLowerCase()) && 
      c.id !== editingCluster?.id
    );
    if (existingCluster) {
      if (existingCluster.cluster_code.toLowerCase() === formData.cluster_code.toLowerCase()) {
        newErrors.cluster_code = 'A cluster with this code already exists';
      }
      if (existingCluster.cluster_name.toLowerCase() === formData.cluster_name.toLowerCase()) {
        newErrors.cluster_name = 'A cluster with this name already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const requestData = {
        ...formData,
        cluster_owner: 1, // TODO: Get from auth context
        cluster_level: formData.parent_cluster_id ? 2 : 1, // Simple level calculation
        created_by: 1
      };

      if (editingCluster) {
        await mockInventoryService.updateCluster(editingCluster.id, requestData);
      } else {
        await mockInventoryService.createCluster(requestData);
      }

      await loadData();
      setShowForm(false);
      setEditingCluster(null);
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error('Error saving inventory cluster:', error);
      swalError('Failed to save cluster. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => {
    setShowForm(false);
    setEditingCluster(null);
    setFormData(initialFormData);
    setErrors({});
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'IN_PROGRESS': return '#007bff';
      case 'PLANNED': return '#ffc107';
      case 'DEPRECATED': return '#dc3545';
      case 'ARCHIVED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'CRITICAL': return '#dc3545';
      case 'HIGH': return '#fd7e14';
      case 'MEDIUM': return '#ffc107';
      case 'LOW': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getParentClusterName = (parentId: number): string => {
    const parent = clusters.find(c => c.id === parentId);
    return parent?.cluster_name || 'None';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Inventory Clusters Management</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          {!showForm ? (
            <div className="list-view">
              <div className="list-header">
                <div className="list-title">
                  <h3>Manage Inventory Clusters</h3>
                  <p>Organize inventory items into logical business and technical clusters</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => setViewMode('grid')}
                      data-testid="BTN_inventory_cluster_view_grid"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: viewMode === 'grid' ? '#007bff' : 'white',
                        color: viewMode === 'grid' ? 'white' : '#333',
                        cursor: 'pointer'
                      }}
                    >
                      Grid
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      data-testid="BTN_inventory_cluster_view_table"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: viewMode === 'table' ? '#007bff' : 'white',
                        color: viewMode === 'table' ? 'white' : '#333',
                        cursor: 'pointer'
                      }}
                    >
                      Table
                    </button>
                  </div>
                  <button
                    className="add-button"
                    onClick={handleAddClick}
                    disabled={loading}
                    data-testid="BTN_inventory_cluster_add"
                  >
                    <span>+</span>
                    Add New Cluster
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading inventory clusters...</p>
                </div>
              ) : clusters.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🗂️</div>
                  <h3>No Clusters Found</h3>
                  <p>Start by creating your first inventory cluster to organize your components.</p>
                  <button className="create-button" onClick={handleAddClick} data-testid="BTN_inventory_cluster_create_empty">
                    Create First Cluster
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="items-grid">
                  {clusters.map((cluster) => (
                    <div key={cluster.id} className="item-card">
                      {/* Header Row: Code + Name */}
                      <div className="data_card_header">
                        <div className="code-name-row">
                          <span className="item-code">{cluster.cluster_code || 'NO-CODE'}</span>
                          <span className="item-name">{cluster.cluster_name || 'NO-NAME'}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="data_default">
                        {cluster.cluster_description || 'No description provided'}
                      </div>

                      {/* Tags and Status */}
                      <div className="data_tags">
                        <span>Status: {cluster.cluster_status}</span>
                        <span> • </span>
                        <span>Type: {cluster.cluster_type}</span>
                        <span> • </span>
                        <span>Priority: {cluster.priority_level}</span>
                        <span> • </span>
                        <span>Progress: {cluster.completion_percentage}%</span>
                        <span> • </span>
                        <span>Team: {cluster.responsible_team || 'Unassigned'}</span>
                      </div>

                      {/* Footer with Path and Actions */}
                      <div className="data_card_footer">
                        <div className="data_paths">
                          🎯 Cluster: {cluster.cluster_code} ({cluster.member_count || 0} items)
                        </div>
                        <div style={{display: 'flex', gap: '8px', marginLeft: 'auto'}}>
                          <button
                            className="edit-button"
                            onClick={() => handleEditClick(cluster)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_cluster_row_${cluster.id}_edit`}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(cluster)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_cluster_row_${cluster.id}_delete`}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Table View */
                <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Type</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Priority</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Progress</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Team</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clusters.map((cluster, index) => (
                        <tr key={cluster.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                          <td style={{ padding: '12px' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
                                {cluster.cluster_name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                {cluster.cluster_code}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '13px' }}>{cluster.cluster_type}</td>
                          <td style={{ padding: '12px' }}>
                            <span 
                              style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: getStatusColor(cluster.cluster_status) + '20',
                                color: getStatusColor(cluster.cluster_status),
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              {cluster.cluster_status}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span 
                              style={{
                                color: getPriorityColor(cluster.priority_level),
                                fontWeight: '600',
                                fontSize: '13px'
                              }}
                            >
                              {cluster.priority_level}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ 
                                width: '60px', 
                                height: '8px', 
                                backgroundColor: '#e9ecef', 
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  width: `${cluster.completion_percentage}%`, 
                                  height: '100%', 
                                  backgroundColor: '#28a745'
                                }}></div>
                              </div>
                              <span style={{ fontSize: '12px', fontWeight: '500', minWidth: '35px' }}>
                                {cluster.completion_percentage}%
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '13px' }}>
                            {cluster.responsible_team || 'Unassigned'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => handleEditClick(cluster)}
                                data-testid={`BTN_inventory_cluster_table_row_${cluster.id}_edit`}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                                disabled={submitting}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteClick(cluster)}
                                data-testid={`BTN_inventory_cluster_table_row_${cluster.id}_delete`}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                                disabled={submitting}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="form-view">
              <div className="form-header">
                <h3>{editingCluster ? 'Edit Cluster' : 'Add New Cluster'}</h3>
                <button type="button" className="back-button" onClick={handleBackClick} data-testid="BTN_inventory_cluster_back">
                  ← Back to List
                </button>
              </div>

              <form onSubmit={handleSubmit} className="inventory-form" data-testid="MOD_inventory_cluster_form">
                <div className="form-grid">
                  <FormControl.Input
                    label="Cluster Code"
                    required
                    name="cluster_code"
                    data-testid="INP_inventory_cluster_code"
                    value={formData.cluster_code}
                    onChange={handleInputChange}
                    error={errors.cluster_code}
                    disabled={submitting}
                    placeholder="e.g., AUTH_MODULE, USER_MGMT, API_CORE"
                    maxLength={50}
                    helper="Unique identifier for this cluster"
                  />

                  <FormControl.Input
                    label="Cluster Name"
                    required
                    name="cluster_name"
                    data-testid="INP_inventory_cluster_name"
                    value={formData.cluster_name}
                    onChange={handleInputChange}
                    error={errors.cluster_name}
                    disabled={submitting}
                    placeholder="e.g., Authentication Module, User Management, Core API"
                    maxLength={100}
                    helper="Descriptive name for this cluster"
                  />

                  <FormControl.SearchSelect
                    label="Cluster Type"
                    required
                    data-testid="SEL_inventory_cluster_type"
                    options={[
                      { value: 'MODULE', label: 'Module' },
                      { value: 'FEATURE', label: 'Feature' },
                      { value: 'SYSTEM', label: 'System' },
                      { value: 'WORKFLOW', label: 'Workflow' },
                      { value: 'COMPONENT', label: 'Component' },
                    ]}
                    value={formData.cluster_type}
                    onChange={v => setFormData(prev => ({ ...prev, cluster_type: String(v ?? 'MODULE') as FormData['cluster_type'] }))}
                    disabled={submitting}
                    helper="Type of logical grouping"
                  />

                  <FormControl.SearchSelect
                    label="Status"
                    required
                    data-testid="SEL_inventory_cluster_status"
                    options={[
                      { value: 'PLANNED', label: 'Planned' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'DEPRECATED', label: 'Deprecated' },
                      { value: 'ARCHIVED', label: 'Archived' },
                    ]}
                    value={formData.cluster_status}
                    onChange={v => setFormData(prev => ({ ...prev, cluster_status: String(v ?? 'PLANNED') as FormData['cluster_status'] }))}
                    disabled={submitting}
                    helper="Current development status"
                  />

                  <FormControl.SearchSelect
                    label="Priority Level"
                    required
                    data-testid="SEL_inventory_cluster_priority"
                    options={[
                      { value: 'LOW', label: 'Low' },
                      { value: 'MEDIUM', label: 'Medium' },
                      { value: 'HIGH', label: 'High' },
                      { value: 'CRITICAL', label: 'Critical' },
                    ]}
                    value={formData.priority_level}
                    onChange={v => setFormData(prev => ({ ...prev, priority_level: String(v ?? 'MEDIUM') as FormData['priority_level'] }))}
                    disabled={submitting}
                    helper="Business/technical priority"
                  />

                  <FormControl.SearchSelect
                    label="Complexity Estimate"
                    data-testid="SEL_inventory_cluster_complexity"
                    options={[
                      { value: 'LOW', label: 'Low' },
                      { value: 'MEDIUM', label: 'Medium' },
                      { value: 'HIGH', label: 'High' },
                      { value: 'CRITICAL', label: 'Critical' },
                    ]}
                    value={formData.complexity_estimate}
                    onChange={v => setFormData(prev => ({ ...prev, complexity_estimate: String(v ?? 'MEDIUM') as FormData['complexity_estimate'] }))}
                    disabled={submitting}
                    helper="Overall complexity assessment"
                  />

                  <div className="form-group">
                    <label htmlFor="business_domain">
                      Business Domain
                      <span className="field-hint">Business area or domain</span>
                    </label>
                    <input
                      id="business_domain"
                      name="business_domain"
                      type="text"
                      value={formData.business_domain}
                      onChange={handleInputChange}
                      disabled={submitting}
                      placeholder="e.g., User Management, Compliance, Reporting"
                      data-testid="INP_inventory_cluster_business_domain"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="responsible_team">
                      Responsible Team
                      <span className="field-hint">Team or person responsible</span>
                    </label>
                    <input
                      id="responsible_team"
                      name="responsible_team"
                      type="text"
                      value={formData.responsible_team}
                      onChange={handleInputChange}
                      disabled={submitting}
                      placeholder="e.g., Frontend Team, Backend Team, DevOps"
                      data-testid="INP_inventory_cluster_responsible_team"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="parent_cluster_id">
                      Parent Cluster
                      <span className="field-hint">Parent cluster (for hierarchical organization)</span>
                    </label>
                    <select
                      id="parent_cluster_id"
                      name="parent_cluster_id"
                      value={formData.parent_cluster_id}
                      onChange={handleInputChange}
                      disabled={submitting}
                      data-testid="SEL_inventory_cluster_parent"
                    >
                      <option value={0}>No Parent (Top Level)</option>
                      {clusters
                        .filter(c => c.id !== editingCluster?.id) // Don't allow self-reference
                        .map((cluster) => (
                          <option key={cluster.id} value={cluster.id}>
                            {cluster.cluster_name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="completion_percentage">
                      Completion Percentage
                      <span className="field-hint">Current progress (0-100%)</span>
                    </label>
                    <input
                      id="completion_percentage"
                      name="completion_percentage"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.completion_percentage}
                      onChange={handleInputChange}
                      className={errors.completion_percentage ? 'error' : ''}
                      disabled={submitting}
                      data-testid="INP_inventory_cluster_completion_percentage"
                    />
                    {errors.completion_percentage && <div className="error-message">{errors.completion_percentage}</div>}
                  </div>

                  <FormControl.Textarea
                    label="Description"
                    required
                    name="cluster_description"
                    data-testid="INP_inventory_cluster_description"
                    value={formData.cluster_description}
                    onChange={handleInputChange}
                    error={errors.cluster_description}
                    disabled={submitting}
                    rows={4}
                    placeholder="Describe what this cluster encompasses, its business purpose, and key components..."
                    maxLength={1000}
                    className="full-width"
                    helper="Detailed description of this cluster's purpose and scope"
                  />

                  <FormControl.Textarea
                    label="Documentation Notes"
                    name="documentation_notes"
                    data-testid="INP_inventory_cluster_documentation_notes"
                    value={formData.documentation_notes}
                    onChange={handleInputChange}
                    disabled={submitting}
                    rows={3}
                    placeholder="Add any additional notes, documentation links, or important information..."
                    className="full-width"
                    helper="Additional notes, links, or documentation references"
                  />

                  <FormControl.Checkbox
                    label="Active Cluster"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={checked => setFormData(prev => ({ ...prev, is_active: checked }))}
                    disabled={submitting}
                    helper="Inactive clusters won't be available for new associations"
                    data-testid="CHK_inventory_cluster_is_active"
                  />
                </div>

                <div className="form-actions">
                  <Button variant="outline" type="button" onClick={handleBackClick} disabled={submitting} data-testid="BTN_inventory_cluster_cancel">Cancel</Button>
                  <Button
                    type="submit"
                    variant={editingCluster ? 'update' : 'fill'}
                    icon={editingCluster ? Check : Save}
                    disabled={submitting}
                    data-testid="BTN_inventory_cluster_submit"
                  >
                    {submitting ? (editingCluster ? 'Updating...' : 'Submitting...') : editingCluster ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryClustersModal;